"use client"

import { Trans } from "@lingui/react/macro"
import { cn } from "@rarible_int/anaconda_ui_components"
import { useCallback } from "react"
import { type GridListProps, VirtuosoGrid } from "react-virtuoso"

interface Props<I> {
  loadNextPage(): void
  items: I[]
  computeItemKey(index: number, item: I): string
  ItemContent: React.ComponentType<{ item: I }>
  initialItemCount?: number
}

export function Grid<T>({
  loadNextPage,
  ItemContent,
  items,
  computeItemKey,
  initialItemCount,
}: Props<T>) {
  const itemContent = useCallback(
    (_: number, item: T) => <ItemContent item={item} />,
    [ItemContent],
  )

  return (
    <VirtuosoGrid
      components={GRID_COMPONENTS}
      data={items}
      computeItemKey={computeItemKey}
      itemContent={itemContent}
      endReached={loadNextPage}
      increaseViewportBy={1200}
      useWindowScroll
      initialItemCount={
        initialItemCount && Math.min(initialItemCount, items.length)
      }
    />
  )
}

export function EmptyGrid({
  EmptyMessage,
}: { EmptyMessage?: React.ComponentType }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4">
      {EmptyMessage ? (
        <EmptyMessage />
      ) : (
        <div className="flex max-w-[600px] flex-col items-center gap-2">
          <div className="flex flex-col items-center gap-1">
            <h2 className="items-center text-lg">
              <Trans>No items found</Trans>
            </h2>
          </div>
        </div>
      )}
    </div>
  )
}

export function GridLayout({
  className,
  ...props
}: Omit<GridListProps, "data-testid">) {
  return (
    <div
      className={cn(
        className,
        "grid grid-cols-2 gap-[10px] sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 xl:grid-cols-7",
      )}
      {...props}
    />
  )
}

const GRID_COMPONENTS = {
  List: GridLayout,
}
